import { Event } from "@/data/events";

/**
 * Calendar export utilities.
 *
 * Times are anchored to America/Chicago (Central Time) regardless of where the
 * viewer is located. The stored event.startTime / endTime strings (e.g. "8:30 AM")
 * represent local Central Time as entered by admins. We emit calendar files using
 * floating local times paired with an explicit TZID=America/Chicago so DST is
 * handled correctly by the recipient's calendar app.
 */

const TZID = "America/Chicago";

// Parse "8:30 AM" / "12:00 PM" -> { hours: 0-23, minutes: 0-59 }
const parseTime = (timeStr: string): { hours: number; minutes: number } => {
  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return { hours: 0, minutes: 0 };
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === "PM" && hours !== 12) hours += 12;
  if (period === "AM" && hours === 12) hours = 0;
  return { hours, minutes };
};

// Format a date + time as a floating local datetime: YYYYMMDDTHHMMSS
const formatFloatingLocal = (date: Date, timeStr: string): string => {
  const { hours, minutes } = parseTime(timeStr);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(hours).padStart(2, "0");
  const mi = String(minutes).padStart(2, "0");
  return `${yyyy}${mm}${dd}T${hh}${mi}00`;
};

// Format as YYYY-MM-DDTHH:MM:SS (used by Outlook & Google with ctz)
const formatIsoLocal = (date: Date, timeStr: string): string => {
  const { hours, minutes } = parseTime(timeStr);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(hours).padStart(2, "0");
  const mi = String(minutes).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:00`;
};

// Format as YYYYMMDDTHHMMSSZ (UTC stamp for DTSTAMP)
const formatUtcStamp = (date: Date): string => {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const hh = String(date.getUTCHours()).padStart(2, "0");
  const mi = String(date.getUTCMinutes()).padStart(2, "0");
  const ss = String(date.getUTCSeconds()).padStart(2, "0");
  return `${yyyy}${mm}${dd}T${hh}${mi}${ss}Z`;
};

// Derive an RRULE string from event metadata, or null for non-recurring.
const getRrule = (event: Event): string | null => {
  if (!event.isRecurring) return null;
  if (event.type === "weekly") return "FREQ=WEEKLY;BYDAY=WE";
  if (event.type === "monthly") return "FREQ=MONTHLY;BYDAY=-1WE";
  return null;
};

const getFullLocation = (event: Event): string => {
  return event.locationAddress
    ? `${event.location}, ${event.locationAddress}`
    : event.location;
};

const slugify = (str: string): string =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/**
 * Google Calendar pre-fill URL.
 * Uses ctz parameter for timezone interpretation of floating times.
 */
export const buildGoogleCalendarUrl = (event: Event): string => {
  const start = formatFloatingLocal(event.date, event.startTime);
  const end = formatFloatingLocal(event.date, event.endTime);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${start}/${end}`,
    details: event.description,
    location: getFullLocation(event),
    ctz: TZID,
  });
  const rrule = getRrule(event);
  if (rrule) {
    // Google accepts a recur param with RRULE: prefix
    params.append("recur", `RRULE:${rrule}`);
  }
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Outlook.com (Outlook Web) pre-fill URL.
 * Outlook Web does not support recurrence via URL — recurring events will be
 * added as a single occurrence here. Users wanting recurrence on Outlook
 * should use the .ics download instead.
 */
export const buildOutlookWebUrl = (event: Event): string => {
  const start = formatIsoLocal(event.date, event.startTime);
  const end = formatIsoLocal(event.date, event.endTime);
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: event.title,
    body: event.description,
    location: getFullLocation(event),
    startdt: start,
    enddt: end,
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

// Escape ICS text per RFC 5545: backslash, comma, semicolon, newlines.
const escapeIcsText = (str: string): string =>
  str
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");

/**
 * Build a full VCALENDAR/VEVENT ICS string for the event.
 * Uses TZID=America/Chicago for DTSTART/DTEND so DST is handled by the
 * recipient's calendar. Includes a minimal VTIMEZONE block with US Central
 * standard/daylight rules.
 */
export const buildIcsFile = (event: Event): string => {
  const dtStart = formatFloatingLocal(event.date, event.startTime);
  const dtEnd = formatFloatingLocal(event.date, event.endTime);
  const dtStamp = formatUtcStamp(new Date());
  const uid = `${event.id}@rcnmo.com`;
  const rrule = getRrule(event);

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//RCN//Events//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VTIMEZONE",
    `TZID:${TZID}`,
    "BEGIN:STANDARD",
    "DTSTART:19701101T020000",
    "RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU",
    "TZOFFSETFROM:-0500",
    "TZOFFSETTO:-0600",
    "TZNAME:CST",
    "END:STANDARD",
    "BEGIN:DAYLIGHT",
    "DTSTART:19700308T020000",
    "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU",
    "TZOFFSETFROM:-0600",
    "TZOFFSETTO:-0500",
    "TZNAME:CDT",
    "END:DAYLIGHT",
    "END:VTIMEZONE",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART;TZID=${TZID}:${dtStart}`,
    `DTEND;TZID=${TZID}:${dtEnd}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
    `DESCRIPTION:${escapeIcsText(event.description)}`,
    `LOCATION:${escapeIcsText(getFullLocation(event))}`,
  ];

  if (rrule) lines.push(`RRULE:${rrule}`);
  if (event.externalLink) lines.push(`URL:${event.externalLink}`);

  lines.push("END:VEVENT", "END:VCALENDAR");

  // RFC 5545 prefers CRLF line endings.
  return lines.join("\r\n");
};

/**
 * Trigger a download of the .ics file for the event.
 * On iOS / macOS this opens directly into Apple Calendar.
 * On Windows this opens Outlook (or default handler).
 */
export const downloadIcs = (event: Event): void => {
  const ics = buildIcsFile(event);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${slugify(event.title) || "event"}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Defer revoke so iOS Safari can hand off to Calendar.app
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
